"use client"

import { useCallback, useEffect, useRef } from "react"
import { useVoiceTranscriptionStore } from "@/stores/use-voice-transcription-store"
import { createPortal } from "react-dom"
import { useHotkeys } from "react-hotkeys-hook"

import { useTranscriptionKeywords } from "@/hooks/use-transcription-keywords"

import { VoiceTranscriptionPill, VoiceTranscriptionPillRef } from "./floating-pill"

export function GlobalVoiceTranscription() {
  const pillRef = useRef<VoiceTranscriptionPillRef>(null)
  const {
    isActive,
    focusedElement,
    selectionStart,
    selectionEnd,
    trigger,
    close,
    registerStopCallback,
    unregisterStopCallback,
  } = useVoiceTranscriptionStore()
  const keywords = useTranscriptionKeywords()

  const handleStop = useCallback(() => {
    pillRef.current?.stopAndInsert()
  }, [])

  useEffect(() => {
    if (isActive) {
      registerStopCallback(handleStop)
    }
    return () => {
      unregisterStopCallback()
    }
  }, [isActive, handleStop, registerStopCallback, unregisterStopCallback])

  useHotkeys(
    "meta+o",
    (event) => {
      event.preventDefault()
      if (isActive && pillRef.current) {
        pillRef.current.stopAndInsert()
        return
      }
      trigger()
    },
    { enableOnFormTags: true, enableOnContentEditable: true },
    [isActive, trigger]
  )

  if (!isActive) return null

  return createPortal(
    <div role="region" aria-label="Voice transcription" className="fixed bottom-8 left-1/2 z-[10000] -translate-x-1/2">
      <VoiceTranscriptionPill
        ref={pillRef}
        onClose={close}
        focusedElement={focusedElement}
        selectionStart={selectionStart}
        selectionEnd={selectionEnd}
        keywords={keywords}
      />
    </div>,
    document.body
  )
}

export default GlobalVoiceTranscription
